import axios from 'axios';
import { toast } from '../lib/toast';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Returns true iff a still-valid OIDC access token currently exists in
// sessionStorage. Used to suppress the "session expired" toast + redirect
// path when a 401 is actually a startup race (a request fired before the
// ApiBridge component's effect had a chance to set the Authorization
// header) rather than a real expiry. Without this check, the first paint
// after sign-in produces a noisy toast and bounces the user to /login
// even though they are perfectly authenticated.
function hasValidOidcSession(): boolean {
  try {
    const key = Object.keys(sessionStorage).find((k) => k.startsWith('oidc.user:'));
    if (!key) return false;
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const u = JSON.parse(raw) as { access_token?: string; expires_at?: number };
    if (!u.access_token) return false;
    const now = Math.floor(Date.now() / 1000);
    return typeof u.expires_at !== 'number' || u.expires_at > now;
  } catch {
    return false;
  }
}

// Response interceptor for error handling. On 401 we clear the persisted
// auth token but stop short of `window.location.href = '/login'`. A hard
// navigation discards unsaved IDE state (open files, dirty buffers, terminal
// scrollback). Instead, dispatch a custom event the auth store can listen for
// to soft-redirect via React router, after the autosave / recovery snapshot
// has flushed.
api.interceptors.response.use(
  (response) => {
    // First success after a previous 401 should dismiss any stale
    // "session expired" toast — otherwise it sticks around indefinitely
    // because react-hot-toast doesn't auto-dismiss errors with a stable id.
    try {
      toast.dismiss('auth-session-expired');
    } catch {
      // Toast subsystem failures must never block a successful response.
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Suppress the toast/redirect for the explicit refresh + heartbeat
      // probes — the session-expiry hook handles those failures itself
      // (often by trying the /auth/me fallback). Without this guard we'd
      // flash the "session expired" toast even while we're mid-attempt to
      // recover the session.
      const requestUrl: string = error.config?.url ?? '';
      const isAuthProbe =
        requestUrl.endsWith('/auth/refresh') || requestUrl.endsWith('/auth/me');

      // Suppress the toast/redirect when a still-valid OIDC session exists
      // in sessionStorage. The 401 is almost certainly a startup race
      // (request fired before ApiBridge's effect set the Authorization
      // header) — calling code should see the 401, retry on its own, and
      // get a fresh 200 once the header lands. We do NOT want to clobber
      // an authenticated user with a "session expired" toast and a
      // /login redirect they didn't ask for.
      const isStartupRace = hasValidOidcSession();

      if (!isAuthProbe && !isStartupRace) {
        try {
          localStorage.removeItem('auth-storage');
        } catch {
          // localStorage can throw in private mode; ignore.
        }
        // Surface a toast so the redirect isn't silent. Use a stable id so
        // multiple in-flight 401s collapse into a single notification.
        try {
          toast.error('Your session expired — please sign in again', {
            id: 'auth-session-expired',
          });
        } catch {
          // Toast failures must never break the redirect path.
        }
        // Fire an event so listening components (router guard, IDE) can react
        // gracefully. Fallback to hard nav after a short delay if nobody handled it.
        try {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        } catch {
          // CustomEvent unsupported — fall through to fallback.
        }
        // Safety net: if 1.5s elapses without a soft redirect, force-redirect.
        // Re-check OIDC validity at fire time — the ApiBridge effect may have
        // landed in the meantime, in which case redirecting an
        // already-authenticated user to /login would be the bug.
        setTimeout(() => {
          if (hasValidOidcSession()) return;
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }, 1500);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API.
//
// `refresh` is the canonical way to bump session lifetime when the user
// clicks "Extend session" in the warning dialog. The session-manager
// service is expected to expose `POST /auth/refresh` returning a fresh
// JWT (same shape as login: `{ token, user }`). If that endpoint doesn't
// exist yet, the `useSessionExpiry` hook detects the 404 and falls back
// to `GET /auth/me` as a heartbeat — many auth middlewares re-issue or
// extend the cookie/token on any authenticated request, which is enough
// to keep the session alive even if no new token is returned.
export const authApi = {
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
};

// Small promise-based sleep used to pace async-grading status polling.
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Scaffolding level type
type ScaffoldingLevel = 'auto' | 'high' | 'medium' | 'low';
type Region = 'base' | 'us' | 'na' | 'eu' | 'asia' | 'mena' | 'oceania';

// Lab API
export const labApi = {
  list: (params?: { published?: boolean; category?: string }) =>
    api.get('/labs', { params }),
  get: (labId: string) => api.get(`/labs/${labId}`),
  getTemplate: (labId: string) => api.get(`/labs/${labId}/template`),
  getTemplateFiles: (labId: string) => api.get(`/labs/${labId}/template/files`),
  getStarterFiles: (labId: string) => api.get(`/labs/${labId}/starter-files`),
  // Browser-tier pytest suite, seeded read-only into the Pyodide workspace.
  getTestFiles: (labId: string) => api.get(`/labs/${labId}/test-files`),
  getSolutionFiles: (labId: string) => api.get(`/labs/${labId}/solution-files`),
  getTestCases: (labId: string) => api.get(`/labs/${labId}/test-cases`),
  start: (labId: string, environmentId?: string) =>
    api.post(`/labs/${labId}/start`, { environmentId }),
  completeActivity: (labId: string) =>
    api.post(`/labs/${labId}/complete-activity`),
  /**
   * Run the lab-owned test suite in the learner's current sandbox. The client
   * never supplies a command: lab-manager resolves the approved runner and
   * returns process/test evidence owned by the server.
   */
  run: (
    labId: string,
    data: { environmentId: string; artifactReleaseId: string },
  ): Promise<unknown> =>
    api
      .post(`/labs/${labId}/run`, data, { timeout: 120000 })
      .then((response) => response.data),
  ensurePortfolioProject: (labId: string) =>
    api
      .post<{
        visibility: 'private';
        project: { id: string };
        gitStatus: {
          available: boolean;
          canCheckpoint: boolean;
          reason: string | null;
        };
      }>(`/labs/${labId}/portfolio`, {})
      .then((response) => response.data),
  createPortfolioCheckpoint: (
    labId: string,
    projectId: string,
    data: {
      runAttemptId: string;
      reflection: string;
      decisionRecord: Record<string, unknown>;
    },
  ) =>
    api
      .post(
        `/labs/${labId}/portfolio/projects/${projectId}/checkpoints`,
        data,
      )
      .then((response) => response.data),
  updateProgress: (labId: string, data: any) =>
    api.patch(`/labs/${labId}/progress`, data),
  /**
   * Record a browser-tier (Tier 0/1) lab submission. The deterministic pytest
   * suite already ran client-side in Pyodide, so this posts the graded outcome
   * + the submitted files to the SAME `/labs/:labId/submit` endpoint, which
   * records the test grade, completion-only result, or pending instructor
   * review WITHOUT provisioning a Firecracker microVM. Only test-graded
   * submissions provide clientOutcome; other modes submit files only.
   * Returns synchronously — no 202/poll.
   */
  submitBrowserGraded: (
    labId: string,
    data: {
      files: Record<string, string>;
      outcome?: {
        passed: boolean;
        score: number;
        maxScore: number;
        tests: Array<{ name: string; passed: boolean; message?: string }>;
      };
    },
  ): Promise<{ gradingResult: unknown | null; mode?: string; status?: 'submitted' }> =>
    api
      .post(
        `/labs/${labId}/submit`,
        {
          files: Object.entries(data.files).map(([path, content]) => ({
            path,
            content,
          })),
          clientOutcome: data.outcome,
        },
        { timeout: 30000 },
      )
      .then((response) => response.data),
  submit: async (
    labId: string,
    data: {
      runAttemptId?: string;
      files:
        | Record<string, string>
        | Record<string, { content: string }>
        | Array<{ path: string; content: string }>;
    }
  ): Promise<{
    gradingResult: any | null;
    mode?: 'manual';
    status?: 'submitted';
  }> => {
    // The lab-manager grader requires `files` as an array of { path, content }.
    // Callers pass a path-keyed Record (shells + IDE), so normalize here in one
    // place. An array is passed through unchanged; a Record<string, string> or
    // Record<string, { content }> is converted to the array shape.
    const { files } = data;
    const normalized = Array.isArray(files)
      ? files
      : Object.entries(files).map(([path, value]) => ({
          path,
          content: typeof value === 'string' ? value : value.content,
        }));

    // Grading is async: submit returns 202 + a submissionId, and the actual
    // grade (fresh Firecracker microVM ~60s cold-boot + sequential pytest execs,
    // ~90s total) exceeds Cloudflare's ~100s edge limit if awaited inline. We
    // POST, then poll getSubmissionStatus until the job resolves. The eventual
    // return shape ({ gradingResult }) is identical to the old synchronous path,
    // so every consumer renders it unchanged.
    //
    // Backward-compat during rolling deploy: if the backend still runs the old
    // synchronous handler it returns 200 with { gradingResult } directly — take
    // that immediately without polling.
    const submitTimeout = 100000; // headroom over the 90s grade, under the edge cap
    const response = await api.post(
      `/labs/${labId}/submit`,
      { files: normalized, runAttemptId: data.runAttemptId },
      { timeout: submitTimeout }
    );

    if (response.data?.gradingResult) {
      // Old synchronous path (or an already-cached result).
      return { gradingResult: response.data.gradingResult };
    }

    // A manually graded lab is a completed submission, not an async grader
    // job. Return the explicit contract to the caller so the learner sees
    // review status instead of a misleading "missing submission id" error.
    if (response.data?.mode === 'manual' && response.data?.status === 'submitted') {
      return { gradingResult: null, mode: 'manual', status: 'submitted' };
    }

    const submissionId: string | undefined = response.data?.submissionId;
    if (!submissionId) {
      throw new Error('Grader did not return a submission id.');
    }

    // Poll for the result. 2.5s cadence × 140 attempts ≈ 6 min ceiling — well
    // past the ~90s grade while still bounding a stuck job.
    const POLL_INTERVAL_MS = 2500;
    const MAX_ATTEMPTS = 140;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await delay(POLL_INTERVAL_MS);
      const status = await labApi.getSubmissionStatus(labId, submissionId);
      if (status.status === 'done') {
        return { gradingResult: status.gradingResult };
      }
      if (status.status === 'error') {
        throw new Error(status.error || 'Grading failed');
      }
      if (status.status === 'expired') {
        throw new Error('Grading result expired — please resubmit.');
      }
      // status === 'grading' → keep polling.
    }
    throw new Error('Grading timed out — please resubmit.');
  },
  // Poll the async grading job created by submit(). 404 (job unknown/pruned or
  // not owned by this user/lab) is surfaced as { status: 'expired' } so the
  // caller can fall back to re-fetching progress rather than treating it as a
  // hard network error.
  getSubmissionStatus: async (
    labId: string,
    submissionId: string
  ): Promise<{
    status: 'grading' | 'done' | 'error' | 'expired';
    gradingResult?: any;
    error?: string;
  }> => {
    try {
      const res = await api.get(
        `/labs/${labId}/submissions/${submissionId}`
      );
      return res.data;
    } catch (err) {
      if (
        (err as { response?: { status?: number } })?.response?.status === 404
      ) {
        return { status: 'expired' };
      }
      throw err;
    }
  },
  getProgress: (labId?: string) =>
    api.get(labId ? `/labs/${labId}/progress` : '/progress'),
  // Tier 3 (gpu) batch fine-tuning job queue. Unlike submit()/getSubmissionStatus,
  // these two are deliberately non-blocking single calls — GpuRuntime.run()
  // owns the poll loop itself so it can yield an OutputEvent between polls
  // instead of resolving one Promise only once the whole job is done.
  submitGpuJob: (
    labId: string,
    data: { jobType: 'lora_finetune' | 'qlora_finetune' | 'embedding_finetune'; jobSpec: Record<string, unknown> },
  ): Promise<
    | { jobId: string; status: 'queued'; queuePosition: number | null }
    | { mode: 'content-view'; jobId: null }
  > =>
    api.post(`/labs/${labId}/gpu-jobs`, data).then((response) => response.data),
  getGpuJobStatus: (
    labId: string,
    jobId: string,
  ): Promise<{
    status: 'queued' | 'claimed' | 'running' | 'done' | 'error' | 'timeout' | 'cancelled' | 'not_found';
    queuePosition?: number | null;
    progress?: Record<string, unknown> | null;
    result?: Record<string, unknown>;
    error?: string;
  }> =>
    api.get(`/labs/${labId}/gpu-jobs/${jobId}`).then((response) => response.data),
  downloadGpuJobArtifact: (labId: string, jobId: string): Promise<Blob> =>
    api.get(`/labs/${labId}/gpu-jobs/${jobId}/artifact`, { responseType: 'blob' })
      .then((response) => response.data),
  // Re-attach. The jobId from submitGpuJob lives only in a mounted component,
  // so without this a learner who closes the tab — which under the deferred
  // batch model is the NORMAL case, since their result lands hours later —
  // has no way back to their own job. Returns identity and status only; the
  // result and artifact stay behind the per-job routes.
  listGpuJobs: (
    labId: string,
  ): Promise<{
    jobs?: Array<{
      jobId: string;
      status: 'queued' | 'claimed' | 'running' | 'done' | 'error' | 'timeout' | 'cancelled';
      jobType: string;
      queuedAt: number;
      completedAt: number | null;
      error: string | null;
    }>;
    mode?: 'content-view';
  }> =>
    api.get(`/labs/${labId}/gpu-jobs`).then((response) => response.data),
  // Code formatting
  formatCode: (environmentId: string, data: {
    code: string;
    filename: string;
    formatter: 'black' | 'prettier';
    options?: { tabSize?: number; useTabs?: boolean };
  }) => api.post(`/environments/${environmentId}/format`, data),
  // Diff - compare student code with solution
  getDiff: (labId: string, filename: string, studentCode: string) =>
    api.post(`/labs/${labId}/diff`, { filename, studentCode }),

  // Region-aware instructions.md. Falls back to base instructions if the
  // student's region doesn't have a localised variant on disk.
  getInstructions: (labId: string, options?: { region?: Region }) =>
    api.get(`/labs/${labId}/instructions`, { params: options }),

  // Scaffolded starter files - adaptive based on student skill and preferences
  getScaffoldedFiles: (labId: string, options?: {
    scaffolding?: ScaffoldingLevel;
    region?: Region;
  }) => api.get(`/labs/${labId}/scaffolded-files`, { params: options }),

  // Student preferences for scaffolding and region
  getPreferences: () => api.get('/preferences').then(res => res.data),
  updateScaffoldingPreference: (preference: ScaffoldingLevel) =>
    api.patch('/preferences/scaffolding', { preference }),
  updateRegionPreference: (region: Region) =>
    api.patch('/preferences/region', { region }),
};

// Catalog API — public course/certificate/lab catalog served by lab-manager.
// Used to build the real course manifest (see services/courseManifest.ts).
export const catalogApi = {
  // Catalog index — list all published certificates / courses (the path-grouped
  // catalog page). Both are public and already populated (cert → course → lab).
  getCertificates: (params?: { search?: string; featured?: boolean; limit?: number; offset?: number }) =>
    api.get('/catalog/certificates', { params }),
  getCourses: (params?: { certificateId?: string; difficulty?: string; search?: string; limit?: number; offset?: number }) =>
    api.get('/catalog/courses', { params }),
  getCourse: (slug: string) => api.get(`/catalog/courses/${slug}`),
  getLabs: (params?: { courseId?: string; difficulty?: string; limit?: number; offset?: number }) =>
    api.get('/catalog/labs', { params }),
  // Ordered mixed manifest (lecture/reading/lab/assessment) — A2.
  getCourseManifest: (slug: string) => api.get(`/catalog/courses/${slug}/manifest`),
  // Per-user completion across the course (lectures + labs) — A4. Authed;
  // recomputes server-side. Used to colour outline item status.
  getCourseProgress: (slug: string) => api.get(`/catalog/courses/${slug}/progress`),
  // Study activity for a reader. The server accepts only the enrolled learner
  // and keeps an explicit completion distinct from simply opening the chapter.
  recordReadingProgress: (courseItemId: string, state: 'opened' | 'completed') =>
    api.post(`/catalog/course-items/${encodeURIComponent(courseItemId)}/progress`, { state }),
  // Active reading time (seconds since the last report). Study telemetry only;
  // the server clamps it and it never affects completion.
  recordReadingDwell: (courseItemId: string, dwellSeconds: number) =>
    api.post(`/catalog/course-items/${encodeURIComponent(courseItemId)}/progress`, { state: 'dwell', dwellSeconds }),
  // Final flush on pagehide/unmount: fetch+keepalive survives tab close where
  // a plain XHR is cancelled. Same payload and auth as recordReadingDwell.
  recordReadingDwellKeepalive: (courseItemId: string, dwellSeconds: number): Promise<void> => {
    const auth = (api.defaults.headers.common as Record<string, unknown>)?.Authorization;
    if (typeof fetch !== 'function') return catalogApi.recordReadingDwell(courseItemId, dwellSeconds).then(() => undefined);
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    return fetch(`${base}/catalog/course-items/${encodeURIComponent(courseItemId)}/progress`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(typeof auth === 'string' ? { Authorization: auth } : {}) },
      body: JSON.stringify({ state: 'dwell', dwellSeconds }),
    }).then((r) => {
      if (!r.ok) return Promise.reject({ response: { status: r.status } });
      return undefined;
    });
  },
  // Assessment runner (C0). courseItemId is the manifest item id.
  getAssessment: (courseItemId: string) => api.get(`/catalog/assessments/${courseItemId}`),
  submitAssessment: (courseItemId: string, answers: Record<string, string[]>) =>
    api.post(`/catalog/assessments/${courseItemId}/submit`, { answers }),
};

// Coursebook chapter reader. The lab-manager resolves the learner's stored
// content region server-side, so the client never decides which regional
// variant to retrieve.
export const coursebookApi = {
  getChapter: (courseSlug: string, chapterId: string) =>
    api.get(`/courses/${encodeURIComponent(courseSlug)}/chapters/${encodeURIComponent(chapterId)}`),
};

// Credentials (A6) — the learner's issued certificates + LinkedIn links.
export const credentialApi = {
  list: () => api.get('/users/me/credentials'),
  verify: (credentialNumber: string) => api.get(`/credentials/${credentialNumber}`),
};

// Environment API
export const environmentApi = {
  create: (data: any) => api.post('/environments', data),
  list: (userId: string) => api.get(`/environments/${userId}`),
  get: (userId: string, environmentId: string) =>
    api.get(`/environments/${userId}/${environmentId}`),
  delete: (userId: string, environmentId: string) =>
    api.delete(`/environments/${userId}/${environmentId}`),
  start: (userId: string, environmentId: string) =>
    api.post(`/environments/${userId}/${environmentId}/start`),
  stop: (userId: string, environmentId: string) =>
    api.post(`/environments/${userId}/${environmentId}/stop`),
  restart: (userId: string, environmentId: string) =>
    api.post(`/environments/${userId}/${environmentId}/restart`),
  logs: (userId: string, environmentId: string, lines?: number) =>
    api.get(`/environments/${userId}/${environmentId}/logs`, { params: { lines } }),
  exec: (userId: string, environmentId: string, command: string[]) =>
    api.post(`/environments/${userId}/${environmentId}/exec`, { command }),
  // K8s pod create / Docker pull can take 30-60s on a cold node; default 30s
  // axios timeout fires before provisioning succeeds. 90s gives enough margin
  // without making a genuinely-stuck call wait too long.
  provisionLab: (labId: string) =>
    api.post(`/labs/${labId}/provision`, undefined, { timeout: 90_000 }),
};

// User API
export const userApi = {
  me: () => api.get('/auth/me'),
  updateProfile: (data: any) => api.patch('/auth/me', data),
  getLimits: () => api.get('/users/me/limits'),
  getUsage: () => api.get('/users/me/usage'),
};

// Hint API
export const hintApi = {
  analyze: (data: {
    error: string;
    code: string;
    language: 'python' | 'javascript' | 'typescript';
    labId: string;
  }) => api.post('/hints/analyze', data),

  getProgressiveHints: (data: {
    error: string;
    code: string;
    language: 'python' | 'javascript' | 'typescript';
    attemptCount?: number;
    labId: string;
  }) => api.post('/hints/progressive', data),

  recordFeedback: (data: {
    usageId: string;
    helpful: boolean;
    resolved: boolean;
    feedback?: string;
  }) => api.post('/hints/feedback', data),

  getEffectiveness: (labId: string) =>
    api.get('/hints/effectiveness', { params: { labId } }),

  getCommonErrors: (labId: string) => api.get(`/hints/common-errors/${labId}`),

  getUserHistory: () => api.get('/hints/history'),

  getPatternStats: () => api.get('/hints/patterns/stats'),
};
